/**
 * StorePeriodFilter - Componente partilhado de filtros para área Lojas
 * 
 * Usado em: Dashboard, Vendas, Fecho de Caixa, Depósitos, Controlo Diário
 * 
 * Features:
 * - Seletor de loja (Todas / lista de lojas permitidas)
 * - Seletor de período (Hoje, Ontem, 7 dias, 30 dias, Mês atual, etc.)
 * - Seletor de datas personalizado
 * - Botão de refresh
 * - Respeita RBAC de lojas
 * - Persiste estado via URL params
 */

import React, { useState, useEffect, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Label } from '@/components/ui/label';
import { format } from 'date-fns';
import { pt } from 'date-fns/locale';
import { Store, Calendar, RefreshCw } from 'lucide-react';
import { useStoreMapping } from '@/hooks/useStoreMapping';

// Period options - consistent across all pages
export const PERIOD_OPTIONS = [
  { value: 'today', label: 'Hoje' },
  { value: 'yesterday', label: 'Ontem' },
  { value: 'last7', label: 'Últimos 7 dias' },
  { value: 'last30', label: 'Últimos 30 dias' },
  { value: 'this_month', label: 'Mês atual' },
  { value: 'last_month', label: 'Mês anterior' },
  { value: 'this_year', label: 'Ano atual' },
  { value: 'custom', label: 'Personalizado' },
];

// Calculate date range from period - Europe/Lisbon timezone
export const getDateRange = (period, customStart, customEnd) => {
  // Use local date to respect Europe/Lisbon timezone
  const now = new Date();
  const today = format(now, 'yyyy-MM-dd');
  
  switch (period) {
    case 'today':
      return { start: today, end: today };
    case 'yesterday': {
      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);
      return { start: format(yesterday, 'yyyy-MM-dd'), end: format(yesterday, 'yyyy-MM-dd') };
    }
    case 'last7': {
      const d = new Date(now);
      d.setDate(d.getDate() - 6); // Includes today
      return { start: format(d, 'yyyy-MM-dd'), end: today };
    }
    case 'last30': {
      const d = new Date(now);
      d.setDate(d.getDate() - 29); // Includes today
      return { start: format(d, 'yyyy-MM-dd'), end: today };
    }
    case 'this_month': {
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      return { start: format(firstDay, 'yyyy-MM-dd'), end: today };
    }
    case 'last_month': {
      const firstDay = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth(), 0);
      return { start: format(firstDay, 'yyyy-MM-dd'), end: format(lastDay, 'yyyy-MM-dd') };
    }
    case 'this_year': {
      const firstDay = new Date(now.getFullYear(), 0, 1);
      return { start: format(firstDay, 'yyyy-MM-dd'), end: today };
    }
    case 'custom':
      return { 
        start: customStart || today, 
        end: customEnd || today 
      };
    default:
      return { start: today, end: today };
  }
};

// Check if period is a single day
export const isSingleDayPeriod = (period, customStart, customEnd) => {
  if (period === 'today' || period === 'yesterday') return true;
  if (period === 'custom') {
    return customStart === customEnd;
  }
  return false;
};

// Custom date range picker sub-component
const CustomDateRangePicker = ({ startDate, endDate, onStartChange, onEndChange }) => {
  const [startOpen, setStartOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);
  
  return (
    <div className="flex items-center gap-2 flex-wrap mt-3 p-3 bg-slate-50 rounded-lg border">
      <div className="flex items-center gap-2">
        <Label className="text-xs text-slate-500">De:</Label>
        <Popover open={startOpen} onOpenChange={setStartOpen}>
          <PopoverTrigger asChild>
            <Button 
              variant="outline" 
              size="sm" 
              className="w-[130px] justify-start text-left font-normal"
              data-testid="custom-date-start"
            >
              <Calendar className="mr-2 h-4 w-4" />
              {startDate ? format(new Date(startDate), 'dd/MM/yyyy') : 'Início'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <CalendarComponent
              mode="single"
              selected={startDate ? new Date(startDate) : undefined}
              onSelect={(date) => {
                const newDate = date ? format(date, 'yyyy-MM-dd') : null;
                onStartChange(newDate);
                // Validate: start <= end
                if (newDate && endDate && newDate > endDate) {
                  onEndChange(newDate);
                }
                setStartOpen(false);
              }}
              locale={pt}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>
      
      <div className="flex items-center gap-2">
        <Label className="text-xs text-slate-500">Até:</Label>
        <Popover open={endOpen} onOpenChange={setEndOpen}>
          <PopoverTrigger asChild>
            <Button 
              variant="outline" 
              size="sm" 
              className="w-[130px] justify-start text-left font-normal"
              data-testid="custom-date-end"
            >
              <Calendar className="mr-2 h-4 w-4" />
              {endDate ? format(new Date(endDate), 'dd/MM/yyyy') : 'Fim'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <CalendarComponent
              mode="single"
              selected={endDate ? new Date(endDate) : undefined}
              onSelect={(date) => {
                const newDate = date ? format(date, 'yyyy-MM-dd') : null;
                // Validate: start <= end
                if (newDate && startDate && newDate < startDate) {
                  onStartChange(newDate);
                }
                onEndChange(newDate);
                setEndOpen(false);
              }}
              locale={pt}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};

/**
 * Main StorePeriodFilter component
 * 
 * @param {Object} props
 * @param {string} props.selectedStore - Current selected store ('all' or store code)
 * @param {function} props.onStoreChange - Callback when store changes
 * @param {string} props.period - Current period value
 * @param {function} props.onPeriodChange - Callback when period changes
 * @param {string} props.customStartDate - Custom start date (YYYY-MM-DD)
 * @param {function} props.onCustomStartChange - Callback for custom start
 * @param {string} props.customEndDate - Custom end date (YYYY-MM-DD)
 * @param {function} props.onCustomEndChange - Callback for custom end
 * @param {function} props.onRefresh - Callback for refresh button
 * @param {boolean} props.loading - Loading state for refresh button
 * @param {boolean} props.showAllStores - Whether to show "Todas as lojas" option (default true)
 * @param {boolean} props.compact - Compact mode for smaller spaces
 */
export const StorePeriodFilter = ({
  selectedStore,
  onStoreChange,
  period,
  onPeriodChange,
  customStartDate,
  onCustomStartChange,
  customEndDate,
  onCustomEndChange,
  onRefresh,
  loading = false,
  showAllStores = true,
  compact = false,
}) => {
  const { getStoreName } = useStoreMapping();
  const { user } = useContext(AuthContext);
  const [stores, setStores] = useState([]);
  const [storesLoading, setStoresLoading] = useState(true);
  
  // Load available stores
  useEffect(() => {
    const loadStores = async () => {
      setStoresLoading(true);
      try {
        const res = await axios.get(`${API}/retail-analytics/stores`);
        const storeList = res.data.stores || [];
        setStores(storeList);
        
        // Auto-select single store for restricted users
        if (storeList.length === 1 && !selectedStore) {
          onStoreChange(storeList[0]);
        }
      } catch (err) {
        console.error('Error loading stores:', err);
        // Fallback: try wintouch mappings
        try {
          const res = await axios.get(`${API}/wintouch/mappings`);
          const mappings = res.data || [];
          const storeList = [...new Set(mappings.map(m => m.wintouch_store_code).filter(Boolean))];
          setStores(storeList);
          
          if (storeList.length === 1 && !selectedStore) {
            onStoreChange(storeList[0]);
          }
        } catch {
          setStores(['NORTESH']); // Fallback
        }
      } finally {
        setStoresLoading(false);
      }
    };
    loadStores();
  }, []);
  
  // Check if user is admin (can see all stores)
  const isAdmin = user?.role === 'admin';
  const canSeeAllStores = showAllStores && (isAdmin || stores.length > 1);
  
  return (
    <div className="space-y-0">
      <div className={`flex flex-wrap items-center gap-3 ${compact ? '' : ''}`}>
        {/* Store selector */}
        <Select 
          value={selectedStore || (canSeeAllStores ? 'all' : '')} 
          onValueChange={onStoreChange}
          disabled={storesLoading}
        >
          <SelectTrigger 
            className={compact ? "w-[150px]" : "w-[180px]"} 
            data-testid="store-filter-selector"
          >
            <Store className="w-4 h-4 mr-2 text-slate-500" />
            <SelectValue placeholder="Selecionar loja" />
          </SelectTrigger>
          <SelectContent>
            {canSeeAllStores && (
              <SelectItem value="all">Todas as lojas</SelectItem>
            )}
            {stores.map(store => (
              <SelectItem key={store} value={store}>{getStoreName(store)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        {/* Period selector */}
        <Select value={period} onValueChange={onPeriodChange}>
          <SelectTrigger 
            className={compact ? "w-[150px]" : "w-[180px]"} 
            data-testid="period-filter-selector"
          >
            <Calendar className="w-4 h-4 mr-2 text-slate-500" />
            <SelectValue placeholder="Período" />
          </SelectTrigger>
          <SelectContent>
            {PERIOD_OPTIONS.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        {/* Refresh button */}
        <Button 
          variant="outline" 
          size="icon" 
          onClick={onRefresh}
          disabled={loading}
          data-testid="filter-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>
      
      {/* Custom date range picker */}
      {period === 'custom' && (
        <CustomDateRangePicker
          startDate={customStartDate}
          endDate={customEndDate}
          onStartChange={onCustomStartChange}
          onEndChange={onCustomEndChange}
        />
      )}
    </div>
  );
};

/**
 * Hook to manage store/period filter state with URL persistence
 * 
 * @param {Object} options
 * @param {string} options.defaultPeriod - Default period (default: 'this_month')
 * @param {string} options.defaultStore - Default store (default: 'all')
 * @returns {Object} Filter state and handlers
 */
export const useStorePeriodFilter = (options = {}) => {
  const { defaultPeriod = 'this_month', defaultStore = 'all' } = options;
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Read initial values from URL
  const [selectedStore, setSelectedStore] = useState(searchParams.get('store') || defaultStore);
  const [period, setPeriod] = useState(searchParams.get('period') || defaultPeriod);
  const [customStartDate, setCustomStartDate] = useState(searchParams.get('start') || null);
  const [customEndDate, setCustomEndDate] = useState(searchParams.get('end') || null);
  
  // Sync state from URL when URL params change (e.g., drill-down navigation)
  useEffect(() => {
    const urlStore = searchParams.get('store');
    const urlPeriod = searchParams.get('period');
    const urlStart = searchParams.get('start');
    const urlEnd = searchParams.get('end');
    
    // Update state if URL differs from current state
    if (urlStore !== null && urlStore !== selectedStore) {
      setSelectedStore(urlStore);
    }
    if (urlPeriod !== null && urlPeriod !== period) {
      setPeriod(urlPeriod);
    }
    if (urlStart !== null && urlStart !== customStartDate) {
      setCustomStartDate(urlStart);
    }
    if (urlEnd !== null && urlEnd !== customEndDate) {
      setCustomEndDate(urlEnd);
    }
  }, [searchParams]); // Only depend on searchParams
  
  // Update URL when filters change (but not from URL sync)
  useEffect(() => {
    // Get current URL params to compare
    const currentParams = new URLSearchParams(searchParams);
    const urlPeriod = currentParams.get('period');
    const urlStart = currentParams.get('start');
    const urlEnd = currentParams.get('end');
    
    // If URL has custom period with dates that aren't yet in state, skip this update
    // (the sync effect will run next and update state, then this effect will run again)
    if (urlPeriod === 'custom' && urlStart && urlEnd) {
      if (customStartDate !== urlStart || customEndDate !== urlEnd) {
        // URL has drill-down params but state hasn't synced yet - skip
        return;
      }
    }
    
    const params = new URLSearchParams();
    if (selectedStore && selectedStore !== 'all') params.set('store', selectedStore);
    if (period && period !== defaultPeriod) params.set('period', period);
    if (period === 'custom' && customStartDate) params.set('start', customStartDate);
    if (period === 'custom' && customEndDate) params.set('end', customEndDate);
    
    // Check if params actually changed to avoid infinite loops
    const newParamsStr = params.toString();
    const currentParamsStr = currentParams.toString();
    
    if (newParamsStr !== currentParamsStr) {
      setSearchParams(params, { replace: true });
    }
  }, [selectedStore, period, customStartDate, customEndDate, defaultPeriod, searchParams]);
  
  // Get computed date range
  const dateRange = getDateRange(period, customStartDate, customEndDate);
  const isSingleDay = isSingleDayPeriod(period, customStartDate, customEndDate);
  
  return {
    // State
    selectedStore,
    period,
    customStartDate,
    customEndDate,
    
    // Setters
    setSelectedStore,
    setPeriod,
    setCustomStartDate,
    setCustomEndDate,
    
    // Computed
    dateRange,
    isSingleDay,
    startDate: dateRange.start,
    endDate: dateRange.end,
    
    // Convenience - props to pass to StorePeriodFilter
    filterProps: {
      selectedStore,
      onStoreChange: setSelectedStore,
      period,
      onPeriodChange: setPeriod,
      customStartDate,
      onCustomStartChange: setCustomStartDate,
      customEndDate,
      onCustomEndChange: setCustomEndDate,
    },
  };
};

export default StorePeriodFilter;
